import { useMemo } from "react";
import {
  ComparablesFilterFields,
  FilterDates,
  Filters,
  PriceMode,
  RecordType,
} from "react-migration/domains/comparables";
import { Stores, useGlobalState, useTranslation } from "react-migration/lib";
import { InfoList, Loading } from "@landtechnologies/components";
import { getMeasurementSystemPreference, hasFeature } from "src/js/stores/user/actions";
import { convertStringToDate, formattedDateRange } from "src/js/util/dates";
import { Feature } from "src/js/stores/user";
import { PriceType } from "react-migration/domains/comparables/components/Visualisations/PriceType";
import { hiddenZooplaFields } from "react-migration/domains/comparables/util/hiddenFields";
import {
  disabledEPCFields,
  disabledZooplaFields,
} from "react-migration/domains/comparables/util/DisabledFilters";
import { ZooplaSearchRecordItem, ComparablesSearchRecordItem } from "./";
import { isZoopla } from "react-migration/domains/comparables/util/isZoopla";

type SearchProps = {
  loading: boolean;
  records: RecordType[];
};

export const Search = ({ loading, records }: SearchProps) => {
  const { t } = useTranslation();
  const unitPreference = getMeasurementSystemPreference();
  const [{ filters, priceMode, priceType }, { setFilters, setPriceMode, setPriceType }] =
    useGlobalState(Stores.comparables);

  const disabledFields = isZoopla(priceMode) ? disabledZooplaFields : disabledEPCFields;
  const hiddenFields = isZoopla(priceMode)
    ? hiddenZooplaFields
    : new Set<ComparablesFilterFields>();

  const [startDate, setStartDate] = useMemo(
    () => [
      convertStringToDate(filters.dateFrom),
      (date?: Date) => setFilters({ ...filters, dateFrom: date && formattedDateRange(date) }),
    ],
    [filters, setFilters]
  );
  const [endDate, setEndDate] = useMemo(
    () => [
      convertStringToDate(filters.dateTo),
      (date?: Date) => setFilters({ ...filters, dateTo: date && formattedDateRange(date) }),
    ],
    [filters, setFilters]
  );

  return (
    <div data-testid="comparables-overview-search">
      <span className="atlas-font-semibold atlas-text-xl atlas-text-content-primary">
        {t("sites.card.comparables.search")}
      </span>
      <div className="atlas-py-2">
        <PriceMode
          priceMode={priceMode}
          priceType={priceType}
          setPriceMode={setPriceMode}
          zooplaLicense={hasFeature(Feature.zooplaFullAccess)}
        />
      </div>
      <div className="atlas-py-2">
        <PriceType
          priceType={priceType}
          setPriceType={setPriceType}
          unitPreference={unitPreference}
        />
      </div>
      <div className="atlas-border-b atlas-border-border-divider-subtle atlas-my-2" />
      <FilterDates
        label={t("sites.card.comparables_layer.date_sold")}
        additionalClassNames="atlas-flex-row atlas-items-center atlas-py-2"
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />
      <Filters
        filters={filters}
        disabledFields={disabledFields}
        hiddenFields={new Set<ComparablesFilterFields>(["dateFrom", "dateTo", ...hiddenFields])}
        setFilters={setFilters}
        unitPreference={unitPreference}
      />
      {loading ? (
        <div className="atlas-mt-4 atlas-flex atlas-w-full atlas-h-screen atlas-justify-center atlas-overflow-hidden">
          <Loading />
        </div>
      ) : (
        <div className="atlas-py-3 atlas-text-xs atlas-border-b atlas-border-border-divider-subtle">
          {records?.length
            ? t("sites.card.comparables_layer.showing_the_nr_of_closest_results", {
                resultsLength: records?.length,
              })
            : t("sites.card.comparables_layer.no_transactions_available_for_current_filters")}
        </div>
      )}

      {!loading && records?.length > 0 && (
        <InfoList className="atlas-divide-y atlas-divide-neutral-200 atlas-divide-solid atlas-divide-x-0 atlas-mt-2">
          {records.map((record) =>
            isZoopla(priceMode) ? (
              <ZooplaSearchRecordItem
                key={record.transaction_id}
                record={record}
                unitPreference={unitPreference}
              />
            ) : (
              <ComparablesSearchRecordItem
                key={record.transaction_id}
                record={record}
                unitPreference={unitPreference}
              />
            )
          )}
        </InfoList>
      )}
    </div>
  );
};
